import { useState, useEffect, useCallback, useMemo } from "react";

// Components
import Table from "./Childs/Table";

// Common Components
import Description from "../../Common/Childs/Structure/Description";
import Title from "../../Common/Childs/Structure/Title";
import FormCard from "../../Common/Childs/Structure/FormCard";

// Importing the add icon
import plus from "../../../assets/icons/plus-solid.svg";

// Delete and Form components
import Delete from "./Delete";
import Form from "./Childs/Form";
import { useGlobal } from "../../../context/GlobalContextProvider";
import { useError } from "../../../context/ErrorContextProvider";

// Initial state for the form
const INITIAL_FORM_STATE = {
  pattern_id: 0,
  pattern_name: "",
  art_pack_id: 0,
  uploadedfile: null,
  thumbnail_url: "",
  thumbnail_url_preview: "",
};

/**
 * API service for pattern operations
 */
const patternAPI = {
  getAll: (artPackId) => fetch(`/api/artpack/${artPackId}/pattern`),
  create: (artPackId, data) =>
    fetch(`/api/artpack/${artPackId}/pattern`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
  update: (artPackId, id, data) =>
    fetch(`/api/artpack/${artPackId}/pattern/${id}`, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
  delete: (artPackId, id) =>
    fetch(`/api/artpack/${artPackId}/pattern/${id}`, {
      method: "DELETE",
      headers: { "Content-type": "application/json; charset=UTF-8" },
    }),
};

/**
 * API service for file operations
 */
const fileAPI = {
  getByName: (name) => fetch(`/api/file/name/${name}`),
};

const Index = () => {
  const { artPack, setSpinner } = useGlobal();
  const { showError } = useError();

  const [patterns, setPatterns] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState(INITIAL_FORM_STATE);
  /**
   * Convert image buffer to base64 data URL
   */
  const getImageDataUrl = useCallback(async (name) => {
    if (!name) {
      console.warn("No name provided for getImage.");
      return null;
    }

    try {
      const req = await fileAPI.getByName(name);
      if (!req.ok) {
        throw new Error(`Failed to fetch image. Status: ${req.status}`);
      }

      const data = await req.json();

      if (!data?.image?.data) {
        throw new Error("Invalid image data received.");
      }

      if (data?.image?.data) {
        const uint8Array = new Uint8Array(data.image.data);
        const blob = new Blob([uint8Array], { type: data.type || "image/png" });
        const imageUrl = URL.createObjectURL(blob);
        return imageUrl;
      }


    } catch (error) {
      console.error(`Error in getImage for ${name}:`, error.message);
      return null;
    }
  }, []);

  /**
   * Process image data for patterns
   */
  const processImages = useCallback(async (result) => {
    try {
      const data = await Promise.all(
        result.map(async (el) => {
          const thumbnailUrlPreview = el.arweave ?? (await getImageDataUrl(el.thumbnail_url));
          return {
            id: el.pattern_id,
            pattern_id: el.pattern_id,
            pattern_name: el.pattern_name,
            art_pack_id: el.art_pack_id,
            thumbnail_url: el.thumbnail_url,
            thumbnail_url_preview: thumbnailUrlPreview,
          };
        })
      );
      setPatterns(data);
    } catch (error) {
      console.error("Error processing images:", error.message);
    }
  }, [getImageDataUrl]);

  /**
   * Load pattern data from the API
   */
  const loadTableData = useCallback(async () => {
    if (!artPack?.art_pack_id) return;

    setSpinner(true);
    try {
      const res = await patternAPI.getAll(artPack.art_pack_id);
      if (res.status === 200) {
        const result = await res.json();
        await processImages(result);
      }
    } catch (error) {
      console.error("Error loading patterns:", error);
    } finally {
      setSpinner(false);
    }
  }, [artPack?.art_pack_id, setSpinner, processImages]);

  /**
   * Load data when the component mounts or art pack changes
   */
  useEffect(() => {
    loadTableData();
  }, [loadTableData]);

  /**
   * Create a new pattern
   */
  const createItem = useCallback(async (data) => {
    setSpinner(true);
    try {
      const payload = {
        pattern_name: data.pattern_name,
        thumbnail_url: data.thumbnail_url,
        art_pack_id: artPack?.art_pack_id,
      };
      await patternAPI.create(artPack?.art_pack_id, payload);
      await loadTableData();
      setShowForm(false);
      setFormData(INITIAL_FORM_STATE);
    } catch (error) {
      console.error("Error creating pattern:", error);
    } finally {
      setSpinner(false);
    }
  }, [artPack?.art_pack_id, setSpinner, loadTableData]);

  /**
   * Update an existing pattern
   */
  const updateItem = useCallback(async (data) => {
    try {
      const payload = {
        pattern_name: data.pattern_name,
        thumbnail_url: data.thumbnail_url,
        art_pack_id: artPack?.art_pack_id,
      };
      await patternAPI.update(artPack?.art_pack_id, data.id, payload);
      await loadTableData();
      setShowForm(false);
      setFormData(INITIAL_FORM_STATE);
    } catch (error) {
      console.error("Error updating pattern:", error);
    }
  }, [artPack?.art_pack_id, loadTableData]);

  /**
   * Delete a pattern
   */
  const deleteItem = useCallback(async () => {
    try {
      const res = await patternAPI.delete(artPack?.art_pack_id, formData.id);
      const json = await res.json();
      if (json.message) {
        showError(json.message);
      }
      await loadTableData();
      setShowForm(false);
      setFormData(INITIAL_FORM_STATE);
    } catch (error) {
      console.error("Error deleting pattern:", error);
    }
  }, [artPack?.art_pack_id, formData.id, showError, loadTableData]);

  /**
   * Show form for adding a new pattern
   */
  const addNew = useCallback(() => {
    setShowForm(true);
    setFormData(INITIAL_FORM_STATE);
  }, []);

  /**
   * Show form for editing an existing pattern
   */
  const onEdit = useCallback((data) => {
    setShowForm(true);
    setFormData(data);
  }, []);

  /**
   * Show delete confirmation
   */
  const onDelete = useCallback((data) => {
    setShowForm(false);
    setFormData(data);
  }, []);

  /**
   * Reset form and hide it
   */
  const onDiscard = useCallback(() => {
    setShowForm(false);
    setFormData(INITIAL_FORM_STATE);
  }, []);

  /**
   * Determine if art pack allows editing
   */
  const isEditingAllowed = useMemo(() => !artPack?.approved, [artPack?.approved]);

  /**
   * Render the form if showForm is true
   */
  if (showForm) {
    return (
      <>
        <Title title="Base Images" />
        <Description text="Set the Base Image that your Fund Raisin NFT will use." />
        <FormCard>
          <Form
            handleSubmit={formData?.pattern_name ? updateItem : createItem}
            formData={formData}
            onDiscard={onDiscard}
            typeCheck={formData?.id ? "updateItem" : "createItem"}
            artPackId={artPack?.art_pack_id}
          />
        </FormCard>
      </>
    );
  }

  /**
   * Render delete confirmation if applicable
   */
  if (!showForm && formData?.id && formData?.delete) {
    return (
      <Delete
        onDiscard={onDiscard}
        handleSubmit={deleteItem}
        formData={formData}
      />
    );
  }

  /**
   * Main rendering when not showing the form or delete confirmation
   */
  return (
    <>
      <Title title="Base Images" />
      <Description text="Set the Base Image that your Fund Raisin NFT will use." />

      {isEditingAllowed && (
        <div className="add-btn" onClick={addNew}>
          Add Base Image
          <img src={plus} className="add-icon" alt="Add Base Image icon" />
        </div>
      )}

      <Table
        data={patterns}
        onEdit={onEdit}
        onDelete={onDelete}
        setShowForm={setShowForm}
        setFormData={setFormData}
        isAllowedToEditOrDelete={isEditingAllowed}
      />
    </>
  );
};

export default Index;
